import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { P, cardStyle } from '../styles/theme';

export default function RevenueDashboard() {
  const [allBks,  setAllBks]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('bookings')
        .select('id, booking_date, service_name, client_name, total_amount, status, payment_details')
        .order('booking_date', { ascending: false })
        .limit(2000);
      if (data) setAllBks(data);
      setLoading(false);
    })();
  }, []);

  const completed = allBks.filter(b => b.status === 'completed');
  const now = new Date();
  const todayStart  = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const weekStart   = new Date(now); weekStart.setDate(now.getDate() - 6); weekStart.setHours(0, 0, 0, 0);
  const monthStart  = new Date(now.getFullYear(), now.getMonth(), 1);
  const yearStart   = new Date(now.getFullYear(), 0, 1);
  const sumRev = (arr) => arr.reduce((s, b) => s + Number(b.total_amount || 0), 0);

  const todayRev = sumRev(completed.filter(b => new Date(b.booking_date) >= todayStart));
  const weekRev  = sumRev(completed.filter(b => new Date(b.booking_date) >= weekStart));
  const monthRev = sumRev(completed.filter(b => new Date(b.booking_date) >= monthStart));
  const yearRev  = sumRev(completed.filter(b => new Date(b.booking_date) >= yearStart));
  const avgTicket = completed.length ? sumRev(completed) / completed.length : 0;

  const clientMap = {};
  completed.forEach(b => { clientMap[b.client_name] = (clientMap[b.client_name] || 0) + 1; });
  const totalClients   = Object.keys(clientMap).length;
  const returningCount = Object.values(clientMap).filter(v => v > 1).length;
  const returningRate  = totalClients ? (returningCount / totalClients * 100) : 0;

  const nonCancelled = allBks.filter(b => b.status !== 'cancelled');
  const noShowCount  = allBks.filter(b => b.status === 'no_show').length;
  const noShowRate   = nonCancelled.length ? (noShowCount / nonCancelled.length * 100) : 0;

  const monthly = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    return { label: d.toLocaleString('en-AU', { month: 'short' }), key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, rev: 0, count: 0 };
  });
  completed.forEach(b => {
    const d = new Date(b.booking_date);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const m = monthly.find(x => x.key === key);
    if (m) { m.rev += Number(b.total_amount || 0); m.count += 1; }
  });
  const maxMonthRev = Math.max(...monthly.map(m => m.rev), 1);

  const svcMap = {};
  completed.forEach(b => {
    (b.service_name || 'Unknown').split('·').map(x => x.trim()).forEach(name => {
      if (!svcMap[name]) svcMap[name] = { rev: 0, count: 0 };
      svcMap[name].rev += Number(b.total_amount || 0); svcMap[name].count += 1;
    });
  });
  const topSvcs   = Object.entries(svcMap).sort(([,a],[,b]) => b.rev - a.rev).slice(0, 6);
  const maxSvcRev = Math.max(...topSvcs.map(([,d]) => d.rev), 1);

  const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const dayVol    = Array(7).fill(0);
  completed.forEach(b => { dayVol[new Date(b.booking_date).getDay()] += 1; });
  const maxDayVol  = Math.max(...dayVol, 1);
  const busiestDay = DAY_NAMES[dayVol.indexOf(Math.max(...dayVol))];

  const methodKeys = ['cash', 'card', 'transfer'];
  const methodRev   = Object.fromEntries(methodKeys.map(k => [k, 0]));
  const methodCount = Object.fromEntries(methodKeys.map(k => [k, 0]));
  completed.forEach(b => {
    const pd = b.payment_details;
    if (!pd) return;
    methodKeys.forEach(k => {
      const amt = Number(pd[k] || 0);
      if (amt > 0) { methodRev[k] += amt; methodCount[k]++; }
    });
  });
  const cardGross   = methodRev.card;
  const stripeTotal = cardGross * 0.017 + methodCount.card * 0.30;
  const cardNet     = cardGross - stripeTotal;
  const confirmedWithMethod = completed.filter(b => b.payment_details && Object.keys(b.payment_details).length > 0).length;

  const ACCENT = [P.rose, P.deep, P.amber, P.green, P.muted, '#A0888A'];

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: P.muted }}><p style={{ fontSize: '32px', marginBottom: '12px' }}>💰</p><p>Loading revenue data...</p></div>;

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600 }}>💰 Revenue Dashboard</h2>
        <p style={{ fontSize: '13px', color: P.muted }}>Completed bookings only · Excludes cancelled & no-shows</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '10px' }}>
        {[
          { label: 'Today',      value: todayRev, icon: '📅', sub: `${completed.filter(b => new Date(b.booking_date) >= todayStart).length} appts` },
          { label: 'This Week',  value: weekRev,  icon: '📆', sub: 'last 7 days' },
          { label: 'This Month', value: monthRev, icon: '🗓️', sub: now.toLocaleString('en-AU', { month: 'long' }) },
          { label: 'This Year',  value: yearRev,  icon: '📊', sub: String(now.getFullYear()) },
        ].map(c => (
          <div key={c.label} style={{ ...cardStyle, textAlign: 'center', padding: '18px 12px' }}>
            <p style={{ fontSize: '20px', marginBottom: '2px' }}>{c.icon}</p>
            <p style={{ fontSize: '10px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>{c.label}</p>
            <p style={{ fontSize: '24px', fontWeight: 800, color: P.green, margin: '0 0 3px' }}>${c.value.toFixed(2)}</p>
            <p style={{ fontSize: '10px', color: P.muted }}>{c.sub}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
        {[
          { label: 'Avg Ticket',     value: `$${avgTicket.toFixed(2)}`,    color: P.rose,  sub: `${completed.length} total bookings` },
          { label: 'Returning Rate', value: `${returningRate.toFixed(1)}%`, color: P.deep,  sub: `${returningCount} / ${totalClients} clients` },
          { label: 'No-Show Rate',   value: `${noShowRate.toFixed(1)}%`,    color: noShowRate > 10 ? P.red : P.amber, sub: `${noShowCount} no-shows` },
        ].map(c => (
          <div key={c.label} style={{ ...cardStyle, textAlign: 'center', padding: '14px 8px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>{c.label}</p>
            <p style={{ fontSize: '22px', fontWeight: 800, color: c.color, margin: '0 0 3px' }}>{c.value}</p>
            <p style={{ fontSize: '10px', color: P.muted }}>{c.sub}</p>
          </div>
        ))}
      </div>

      <div style={{ ...cardStyle, marginBottom: '14px' }}>
        <h4 style={{ color: P.deep, marginBottom: '18px', fontWeight: 700 }}>📈 Revenue Trend — Last 12 Months</h4>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '160px' }}>
          {monthly.map(m => (
            <div key={m.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: '3px' }}>
              {m.rev > 0 && <span style={{ fontSize: '8px', color: P.muted, whiteSpace: 'nowrap' }}>${m.rev >= 1000 ? (m.rev/1000).toFixed(1)+'k' : m.rev.toFixed(0)}</span>}
              <div style={{ width: '100%', height: `${Math.max(3, (m.rev / maxMonthRev) * 115)}px`, background: m.rev > 0 ? `linear-gradient(180deg, ${P.rose}, ${P.deep})` : P.border, borderRadius: '3px 3px 0 0', minHeight: '3px' }} />
              <span style={{ fontSize: '8px', color: P.muted, whiteSpace: 'nowrap' }}>{m.label}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${P.border}` }}>
          <span style={{ fontSize: '11px', color: P.muted }}>{monthly.filter(m => m.count > 0).length} active months</span>
          <span style={{ fontSize: '11px', color: P.deep, fontWeight: 700 }}>Peak: ${Math.max(...monthly.map(m => m.rev)).toFixed(2)}</span>
        </div>
      </div>

      {topSvcs.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '14px' }}>
          <h4 style={{ color: P.deep, marginBottom: '16px', fontWeight: 700 }}>💅 Top Services by Revenue</h4>
          {topSvcs.map(([name, data], i) => (
            <div key={name} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: ACCENT[i] + '25', color: ACCENT[i], fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i+1}</span>
                  <span style={{ fontSize: '13px', color: P.text, fontWeight: 600 }}>{name}</span>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: '13px', fontWeight: 800, color: P.deep }}>${data.rev.toFixed(2)}</span>
                  <span style={{ fontSize: '10px', color: P.muted, marginLeft: '6px' }}>{data.count}x</span>
                </div>
              </div>
              <div style={{ height: '6px', background: P.border, borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(data.rev / maxSvcRev) * 100}%`, background: `linear-gradient(90deg, ${ACCENT[i]}, ${ACCENT[i]}99)`, borderRadius: '3px' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: '14px' }}>
        <h4 style={{ color: P.deep, marginBottom: '4px', fontWeight: 700 }}>💳 Revenue by Payment Method</h4>
        <p style={{ fontSize: '12px', color: P.muted, marginBottom: '16px' }}>
          {confirmedWithMethod} of {completed.length} completed bookings have payment method recorded
        </p>
        {[
          { key: 'cash',     label: '💵 Cash',          color: P.green },
          { key: 'card',     label: '💳 Card',          color: P.rose  },
          { key: 'transfer', label: '🏦 Bank Transfer', color: P.deep  },
        ].map(({ key, label, color }) => (
          <div key={key} style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: P.text }}>{label}</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '14px', fontWeight: 800, color }}>${methodRev[key].toFixed(2)}</span>
                <span style={{ fontSize: '11px', color: P.muted, marginLeft: '6px' }}>{methodCount[key]}x</span>
              </div>
            </div>
            <div style={{ height: '6px', background: P.border, borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${yearRev > 0 ? (methodRev[key] / yearRev) * 100 : 0}%`, background: color, borderRadius: '3px' }} />
            </div>
            {key === 'card' && methodCount.card > 0 && (
              <p style={{ fontSize: '11px', color: P.muted, marginTop: '4px' }}>
                Stripe fees: ${stripeTotal.toFixed(2)} · Net: <strong style={{ color: P.green }}>${cardNet.toFixed(2)}</strong>
              </p>
            )}
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        <h4 style={{ color: P.deep, marginBottom: '16px', fontWeight: 700 }}>📊 Booking Volume by Day of Week</h4>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '130px' }}>
          {DAY_NAMES.map((day, i) => {
            const isBusiest = dayVol[i] === Math.max(...dayVol) && dayVol[i] > 0;
            return (
              <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: '4px' }}>
                {dayVol[i] > 0 && <span style={{ fontSize: '10px', color: isBusiest ? P.rose : P.muted, fontWeight: isBusiest ? 700 : 400 }}>{dayVol[i]}</span>}
                <div style={{ width: '100%', height: `${Math.max(3, (dayVol[i] / maxDayVol) * 90)}px`, background: isBusiest ? `linear-gradient(180deg, ${P.rose}, ${P.deep})` : P.peach, borderRadius: '3px 3px 0 0', minHeight: '3px' }} />
                <span style={{ fontSize: '10px', color: isBusiest ? P.rose : P.muted, fontWeight: isBusiest ? 700 : 400 }}>{day}</span>
              </div>
            );
          })}
        </div>
        {Math.max(...dayVol) > 0 && <p style={{ fontSize: '11px', color: P.muted, marginTop: '10px', textAlign: 'center' }}>Busiest day: <strong style={{ color: P.rose }}>{busiestDay}</strong> · {Math.max(...dayVol)} completed bookings</p>}
      </div>
    </div>
  );
}
